import { getCartErrorMessage } from '@theme/cart-feedback';

// Multi-pair bundle picker: tier radios decide how many pair rows show; each row resolves color + size to a variant.
class SBundlePicker extends HTMLElement {
  #ac;
  #variants = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const data = this.querySelector('script[data-bundle-variants]');
    try {
      this.#variants = JSON.parse(data?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.name === 'bundle-tier') this.#syncRows();
      if (target instanceof HTMLSelectElement) this.#syncAvailability();
    }, opts);

    this.addEventListener('command', (event) => {
      if (event.command === '--add-bundle') this.#add(event.source);
      if (event.command === '--upgrade') this.#selectTier(event.source?.dataset.tier);
    }, opts);

    this.#syncRows();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #selectTier(tier) {
    const radio = this.querySelector(`input[name="bundle-tier"][value="${CSS.escape(String(tier || ''))}"]`);
    if (!radio) return;
    radio.checked = true;
    this.#syncRows();
  }

  #quantity() {
    const checked = this.querySelector('input[name="bundle-tier"]:checked');
    return Number.parseInt(checked?.value || '1', 10) || 1;
  }

  #syncRows() {
    const quantity = this.#quantity();
    this.querySelectorAll('[data-pair-row]').forEach((row) => {
      const index = Number.parseInt(row.dataset.pairRow, 10);
      row.hidden = index > quantity;
    });
    this.querySelectorAll('[data-tier-summary]').forEach((el) => {
      el.hidden = el.dataset.tierSummary !== String(quantity);
    });
    this.#syncAvailability();
  }

  #variantFor(row) {
    const selected = [...row.querySelectorAll('select[data-option-index]')].map((select) => select.value);
    return this.#variants.find((variant) => variant.options.every((value, i) => value === selected[i]));
  }

  #syncAvailability() {
    let allAvailable = true;
    this.querySelectorAll('[data-pair-row]').forEach((row) => {
      if (row.hidden) return;
      const variant = this.#variantFor(row);
      const ok = Boolean(variant?.available);
      const note = row.querySelector('[data-pair-unavailable]');
      if (note) note.hidden = ok;
      if (!ok) allAvailable = false;
    });
    const button = this.querySelector('[command="--add-bundle"]');
    if (button) button.setAttribute('aria-disabled', allAvailable ? 'false' : 'true');
  }

  async #add(button) {
    if (this.getAttribute('aria-busy') === 'true') return;
    if (button?.getAttribute('aria-disabled') === 'true') return;

    const counts = new Map();
    for (const row of this.querySelectorAll('[data-pair-row]')) {
      if (row.hidden) continue;
      const variant = this.#variantFor(row);
      if (!variant?.available) return;
      counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    }
    if (!counts.size) return;

    this.setAttribute('aria-busy', 'true');
    this.#showError('');

    try {
      if (window.Shopify?.actions?.updateCart) {
        const lines = [...counts].map(([id, quantity]) => ({ merchandiseId: String(id), quantity }));
        const result = await window.Shopify.actions.updateCart(
          { lines },
          { event: { context: 'product', detail: { source: 's-bundle-picker' } } }
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) {
          this.#showError(message);
          return;
        }
        await window.Shopify.actions.openCart?.();
      } else {
        // Fallback without Standard Actions: the Ajax cart endpoint comes from Liquid routes via data-cart-add-url.
        const items = [...counts].map(([id, quantity]) => ({ id, quantity }));
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error(this.dataset.errorMessage || '');
        window.location.href = this.dataset.cartUrl;
      }
    } catch (error) {
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-bundle-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-bundle-picker')) {
  customElements.define('s-bundle-picker', SBundlePicker);
}
